import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useFreeze } from './freezeOwner.js';

/**
 * @file THE CALL: a portrait and a line of dialogue, over the street the player
 * is standing in.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE WORLD STAYS VISIBLE — THAT IS THE WHOLE FEATURE
 * ─────────────────────────────────────────────────────────────────────────────
 * `Cutscene.jsx` is a full-screen still on a black field: the game goes away and
 * a scene happens. A phone call is the opposite claim — the player is still
 * standing where they were standing, mid-street, mid-anything, and somebody is
 * talking in their ear. So the root is TRANSPARENT: no `bg-void`, no letterbox,
 * no scrim. That single deleted class is the entire visual difference between
 * this file and the cutscene player, and it is the point.
 *
 * ⚠ IT STILL FREEZES THE SIM. Owner decision, and it is not a contradiction:
 * the world is DRAWN at `timeScale 0`, so "whatever you were doing" stays on
 * screen exactly as you left it — it is paused, not hidden. A call that ran the
 * sim underneath would mean reading a story beat while being shot at, and would
 * put a modal reading surface over a live firefight. The freeze goes through
 * `freezeOwner.js` (a per-engine reference count) rather than a bare
 * `setTimeScale(0)`, because a call can end straight into the phone panel and
 * "the last one to restore wins" is a defect this project has already recorded.
 *
 * ⚠ AND IT SWALLOWS ESCAPE, for the same reason `Cutscene.jsx` does: a pause
 * menu opened over a call would restore the time scale to 1 on its own cleanup
 * and leave the world running behind a portrait. Esc ends the call instead.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ⚠⚠ THE PORTRAIT IS THE THING THE REVIEW SCENE EXISTS TO JUDGE
 * ─────────────────────────────────────────────────────────────────────────────
 * The only art is `public/portrait.png` — ~784 × 1160, a figure on an OPAQUE
 * warm-gradient background. Pasted straight down, its rectangle reads as a
 * photograph stuck onto a street, and the warm ground fights every cold HUD
 * token in the palette. So it is dissolved with ONE radial mask.
 *
 * ⚠ ONE RADIAL, NOT TWO LINEARS INTERSECTED. `mask-composite: intersect` /
 * `-webkit-mask-composite: source-in` is the one part of this technique that is
 * not uniformly supported, and a mask that silently fails composites to "no
 * mask" — i.e. the pasted rectangle, in the one browser nobody tested. The
 * RECORDED FALLBACK, if review says the radial crops the chin: stack
 * `linear-gradient(to top, transparent 0%, #000 18%)` with
 * `linear-gradient(to right, transparent 0%, #000 12%, #000 88%, transparent
 * 100%)` and intersect them — more control over the bottom edge, and the
 * compatibility risk above.
 *
 * ⚠⚠ AND A MASK ALONE DOES NOT WORK. This is the trap, and it is why this
 * overlay needs a review scene at all. A fade dissolves the portrait into
 * WHATEVER IS BEHIND IT — which at midnight is near-black and reads beautifully,
 * and at noon on sunlit pavement is near-WHITE, where the figure loses its
 * silhouette completely and the soft edge becomes a smear. The mask cannot fix
 * that, because the mask is the thing causing it. What buys the separation is a
 * soft `radial-gradient` GLOW in `--color-void` behind the portrait: a pool of
 * the HUD's own near-black that the mask can dissolve INTO, independent of the
 * street. **Judge this at noon and at midnight, over pavement and over shadow.
 * A screenshot at one time of day proves nothing.**
 *
 * The glow is drawn as a separate layer under the image rather than as a
 * `background` on the same element, because a background sits INSIDE the mask
 * and would be dissolved by exactly the gradient it exists to compensate for.
 *
 * ⚠ DEGRADATION IS NEVER A BROKEN-IMAGE GLYPH OVER A STORY BEAT. `onError` →
 * an initial in a disc, `Cutscene.jsx`'s idiom. A call whose payload carries no
 * `portrait` renders the same disc — one path, so the missing-art case and the
 * no-art case cannot look different.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * engine → ui  'phone:call'      { token, id, name, caller, speaker, portrait,
 *                                  shot, shots, text, last }
 * engine → ui  'phone:call:end'  { token, cancelled? }        ⚠ two-way
 * ui → engine  'phone:call:next' { token }
 * ui → engine  'phone:call:end'  { token }
 * ```
 *
 * ⚠ THE SHOT CURSOR LIVES IN THE MODEL. `'phone:call'` is REPUBLISHED per shot
 * on the same token; this overlay renders one snapshot and emits
 * `'phone:call:next'`. It keeps no shot index, which is what lets the model skip
 * a beat, branch, or shorten a call without the two disagreeing — the
 * `ShopPanel` pure-renderer rule applied to a cursor. `shot`/`shots` are for
 * PIPS, and `last` is the model's word on whether this is the end.
 *
 * ⚠ `'phone:call:end'` IS TWO-WAY and this listens to the channel it writes to,
 * filtered by `sentRef` so it never hears itself. The model cancels a call from
 * its own side on a teardown, a death, or a mission that outranks it — and
 * without this the portrait is stranded over a FROZEN loop with nothing left to
 * answer it, which is the unrecoverable state.
 *
 * ⚠ NO TYPEWRITER THIS ROUND. If one is ever added: every instant must be
 * derived from ONE `t` fixed at open time and never accumulated (`Speech.js`'s
 * rule — an accumulator drifts against the hold the moment a frame is long or
 * the perf governor halves the render rate, and the drift is invisible until a
 * line finishes typing after it has faded). And because the sim is FROZEN here,
 * it would have to ride wall-clock, not the fixed step — the same reason
 * `Weapons.js` rejected a CSS animation for the reload bar, inverted.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * LAYOUT
 * ─────────────────────────────────────────────────────────────────────────────
 * `z-[62]` — above `Cutscene`'s 60 (a call may cover a briefing), below the
 * phone PANEL's 65 and `ShopPanel`'s 70. Add it to `routes/PlayRoute.jsx`'s
 * layout block when it is mounted.
 *
 * Returns `null` until `'phone:call'` arrives.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

/** Keys that advance the call. The ones people try. */
const NEXT_KEYS = new Set(['Space', 'Enter', 'NumpadEnter', 'KeyE']);

/**
 * The dissolve. ⚠ ONE radial — see the header for why, and for the two-linear
 * fallback. Held as a constant so the `-webkit-` and standard properties cannot
 * drift apart, which is the classic way half a browser matrix loses the mask.
 */
const PORTRAIT_MASK =
  'radial-gradient(ellipse 62% 58% at 50% 38%, #000 45%, rgba(0,0,0,0.75) 68%, transparent 100%)';

/**
 * The pool of near-black the mask dissolves INTO. ⚠ This is what makes the
 * portrait survive a sunlit street — read the header before touching a stop.
 * `color-mix` keeps it tied to the real token rather than shadowing the hex.
 */
const PORTRAIT_GLOW = [
  'radial-gradient(ellipse 58% 52% at 50% 46%,',
  'color-mix(in srgb, var(--color-void) 92%, transparent) 0%,',
  'color-mix(in srgb, var(--color-void) 72%, transparent) 46%,',
  'color-mix(in srgb, var(--color-void) 0%, transparent) 78%)',
].join(' ');

/**
 * IS A CALL ON SCREEN? For the ONE consumer that has to know: `PlayRoute`, which
 * stands the gameplay chrome down while somebody is talking.
 *
 * ⚠⚠ THIS EXISTS BECAUSE A TRANSPARENT OVERLAY IS NEW HERE, AND IT COST A BUG.
 * Every other modal in this game (`PauseMenu`, `Cutscene`, `ShopPanel`,
 * `PhonePanel`) paints an opaque `bg-void` over the whole screen, so the HUD
 * under it has never had to be thought about — it was simply covered. The call
 * is the first overlay that deliberately does NOT do that, because the point of
 * the VN treatment is that you can still see what you were doing.
 *
 * The consequence, found in the browser at `/play/phone` and NOT by any suite:
 * the portrait is anchored bottom-left and so is the entire gameplay column
 * (phone slab, ammo, health, minimap). **The portrait drew straight over the
 * minimap.** No node test could see it — both were correct on their own, and
 * the collision only exists because one of them stopped being opaque.
 *
 * The fix is a judgement, not a nudge: during a frozen story beat a minimap you
 * cannot read, an ammo counter you cannot act on and a mission panel you cannot
 * reach are noise behind a conversation. They step aside, and come back the
 * instant the call ends. ⚠ It is OPACITY, never unmounting — these components
 * hold live bus subscriptions and a remount would drop the very snapshots that
 * make them correct when they return.
 *
 * @param {any} engine
 * @returns {boolean}
 */
export function usePhoneCallActive(engine) {
  const [live, setLive] = useState(false);
  const tokenRef = useRef('');

  useEffect(() => {
    tokenRef.current = '';
    setLive(false);
    const bus = engine?.bus;
    if (!bus?.on) return undefined;
    const offCall = bus.on('phone:call', (p) => {
      if (!p?.token) return;
      tokenRef.current = p.token;
      setLive(true);
    });
    // Both ends answer this: ours and the model's cancel. Either way the chrome
    // must come back, so unlike the component itself this does NOT filter on a
    // `sentRef` — there is nothing here that could be left stranded.
    const offEnd = bus.on('phone:call:end', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!token || tokenRef.current !== token) return;
      tokenRef.current = '';
      setLive(false);
    });
    return () => {
      offCall?.();
      offEnd?.();
    };
  }, [engine]);

  return live;
}

export default function PhoneCall({ engine }) {
  /**
   * THE ONLY STATE: the last shot the model sent. No shot index, no line
   * buffer, no copy of anything.
   * @type {[null|{token: string, text?: string, portrait?: string, shot?: number,
   *   shots?: number, last?: boolean, name?: string, caller?: string,
   *   speaker?: string}, Function]}
   */
  const [scene, setScene] = useState(null);
  /** The portrait src that failed to load, so a retry is per-image not per-shot. */
  const [brokenSrc, setBrokenSrc] = useState('');
  /** Latches the end emit, so one token can only ever be answered once. */
  const sentRef = useRef('');
  const sceneRef = useRef(null);
  /** The live keydown handler. Rebuilt every render, registered exactly once. */
  const keyRef = useRef(null);

  sceneRef.current = scene;

  // ── subscribe ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const bus = engine?.bus;
    if (!bus?.on) return undefined;

    // Every shot arrives on this channel, on the same token. A new token re-arms
    // the latch so a second call can still be answered.
    const offCall = bus.on('phone:call', (p) => {
      if (!p?.token) return;
      if (sentRef.current === p.token) return;
      sceneRef.current = p;
      setScene(p);
    });

    // ⚠ The two-way half. Ours are filtered out by `sentRef`, so this can only
    // ever fire for somebody else's end — a teardown, a death, a mission that
    // outranks the call. Without it the portrait sits over a frozen loop with
    // nothing left to answer it.
    const offEnd = bus.on('phone:call:end', (p) => {
      const token = typeof p === 'string' ? p : p?.token;
      if (!token || sentRef.current === token) return;
      setScene((s) => {
        if (!s || s.token !== token) return s;
        sceneRef.current = null;
        return null;
      });
    });

    return () => {
      offCall?.();
      offEnd?.();
    };
  }, [engine]);

  // A new engine means the old world is gone — never leave a disposed level's
  // call on screen, and never answer its token afterwards.
  useEffect(() => {
    sceneRef.current = null;
    setScene(null);
    setBrokenSrc('');
    sentRef.current = '';
  }, [engine]);

  const live = !!scene;

  // ── freeze the sim ─────────────────────────────────────────────────────────
  // Single-owner, reference counted — see `freezeOwner.js`. ⚠ Keyed on the
  // BOOLEAN, never on `scene`: the model republishes a NEW object per shot, and
  // keying on it would flick the time scale 0 → 1 → 0 on every line.
  useFreeze(engine, 'phone-call', live);

  // ── drop held keys on both edges ───────────────────────────────────────────
  useEffect(() => {
    if (!live) return undefined;
    clearGameInput(engine);
    return () => clearGameInput(engine);
  }, [engine, live]);

  // ── actions ────────────────────────────────────────────────────────────────
  /** Hang up. Answers the model exactly once for this token. */
  const hangUp = useCallback(() => {
    const s = sceneRef.current;
    if (!s) return;
    if (sentRef.current !== s.token) {
      sentRef.current = s.token;
      engine?.bus?.emit?.('phone:call:end', { token: s.token });
    }
    sceneRef.current = null;
    setScene(null);
  }, [engine]);

  /**
   * Next line. ⚠ On a non-last shot this ONLY asks — the model republishes
   * `'phone:call'` with the next shot and that is what changes the screen. It
   * deliberately does not clear the scene, or the portrait would blink out
   * between every line of a conversation.
   */
  const advance = useCallback(() => {
    const s = sceneRef.current;
    if (!s) return;
    if (s.last) {
      hangUp();
      return;
    }
    engine?.bus?.emit?.('phone:call:next', { token: s.token });
  }, [engine, hangUp]);

  // ── keyboard ───────────────────────────────────────────────────────────────
  // ⚠⚠ REGISTERED ONCE, AT MOUNT, IN A LAYOUT EFFECT WITH DEPS `[]`, AND NEVER
  // RE-RUN — `ShopPanel.jsx`'s pattern verbatim. Window capture listeners fire
  // in REGISTRATION ORDER, not by z-index, and `PauseMenu.jsx` registers its own
  // Escape swallower at HUD mount; a listener added when a CALL starts would
  // lose that race and Escape would open a pause menu invisibly underneath, then
  // restore the time scale on its own cleanup.
  // ⚠ `Cutscene.jsx` registers on `[scene, advance, skip]` and therefore LOSES
  // this race — a documented, never-fixed latent bug. Its keyboard code is not
  // the model for this file; only its structure is.
  keyRef.current = (e) => {
    // No call: not our keyboard. Return WITHOUT touching the event — this
    // listener outlives the overlay and shares the node with three others, and
    // swallowing here would eat the whole game's input. Load-bearing, not
    // defensive: it is what lets four capture listeners coexist.
    if (!sceneRef.current) return;

    // A chord belongs to the browser: swallowed from the sim, never acted on,
    // never cancelled (Ctrl+R must still reload).
    if (e.ctrlKey || e.metaKey || e.altKey) {
      e.stopImmediatePropagation();
      return;
    }

    if (e.code === 'Escape') {
      // Swallowed, never forwarded — see the header.
      e.preventDefault();
      e.stopImmediatePropagation();
      hangUp();
      return;
    }

    if (NEXT_KEYS.has(e.code)) {
      e.preventDefault();
      e.stopImmediatePropagation();
      advance();
      return;
    }

    // ⚠ Only `keydown` reaches here (the listener is keydown-only): swallowing
    // keyup would strand a held key in `Input.downKeys` for ever.
    e.stopImmediatePropagation();
  };

  useLayoutEffect(() => {
    const onKeyDown = (e) => keyRef.current?.(e);
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, []);

  if (!scene) return null;

  const portrait = typeof scene.portrait === 'string' && scene.portrait ? scene.portrait : '';
  const broken = !portrait || brokenSrc === portrait;
  const who = firstString(scene.speaker, scene.caller, scene.name);
  const shots = Math.max(0, Math.floor(Number(scene.shots) || 0));
  const shot = Math.max(0, Math.floor(Number(scene.shot) || 0));
  const last = !!scene.last;

  return (
    <div
      data-hud-overlay=""
      onMouseDown={(e) => e.preventDefault()}
      onClick={advance}
      className="hud-fade-in pointer-events-auto absolute inset-0 z-[62] cursor-pointer"
    >
      {/* ── THE PORTRAIT ──────────────────────────────────────────────────────
          Bottom-left, behind the box. The glow is its OWN layer under the image
          — a background on the masked element would be dissolved by the very
          gradient it exists to compensate for. See the header. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-0 select-none"
        style={{ height: 'min(46vh, 30rem)', aspectRatio: '784 / 1160' }}
      >
        <div className="absolute -inset-[18%]" style={{ background: PORTRAIT_GLOW }} />

        {broken ? (
          // Degraded: an initial in a disc. Same path for "no art authored" and
          // "art failed to load", so a story beat never shows a broken glyph.
          <div className="absolute inset-0 flex items-end justify-center pb-[12%]">
            <div className="flex h-24 w-24 items-center justify-center rounded-full border border-hairline bg-ink/85 font-display text-3xl uppercase leading-none text-hud-dim">
              {initialOf(who)}
            </div>
          </div>
        ) : (
          <img
            src={portrait}
            alt=""
            draggable={false}
            onError={() => setBrokenSrc(portrait)}
            className="absolute inset-0 h-full w-full object-contain object-bottom"
            style={{
              WebkitMaskImage: PORTRAIT_MASK,
              maskImage: PORTRAIT_MASK,
              WebkitMaskRepeat: 'no-repeat',
              maskRepeat: 'no-repeat',
              WebkitMaskSize: '100% 100%',
              maskSize: '100% 100%',
            }}
          />
        )}
      </div>

      {/* ── THE LINE ──────────────────────────────────────────────────────────
          A real CARD, not a letterbox gradient: there is no black field behind
          this overlay to fade into, so a gradient would just tint the street and
          leave the text sitting on whatever is under it. */}
      <div className="pointer-events-none absolute inset-x-0 bottom-6 flex justify-center px-4 sm:px-8">
        <div className="pointer-events-auto w-full max-w-3xl rounded-[4px] border border-hairline bg-ink/92 px-5 py-4 shadow-[0_18px_60px_rgba(0,0,0,0.55)] backdrop-blur-[3px] sm:px-7 sm:py-5">
          {who ? (
            <div className="mb-2 font-display text-lg uppercase tracking-[0.2em] text-gold">
              {who}
            </div>
          ) : null}

          {/* ⚠ A FIXED `min-h`, so a one-line and a four-line shot do not make
              the portrait and the pips jump between beats. */}
          <p className="min-h-[4.5rem] font-hud text-[17px] leading-relaxed text-hud sm:text-[19px]">
            {typeof scene.text === 'string' ? scene.text : ''}
          </p>

          <div className="mt-4 flex items-center justify-between gap-4">
            {/* Shot pips: four beats should read as four beats. Straight off
                `shot`/`shots` — this file keeps no cursor of its own. */}
            <div className="flex gap-1.5" aria-hidden="true">
              {Array.from({ length: shots }, (_, i) => (
                <span
                  key={i}
                  className={`h-1 w-7 rounded-sm transition ${i <= shot ? 'bg-gold' : 'bg-hairline'}`}
                />
              ))}
            </div>

            <div className="flex items-center gap-4">
              {!last ? (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    hangUp();
                  }}
                  style={{ touchAction: 'manipulation' }}
                  className="font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint transition hover:text-hud-dim"
                >
                  Skip
                </button>
              ) : null}
              <span className="font-hud text-[11px] uppercase tracking-[0.28em] text-hud-faint">
                {last ? 'Click / Space — hang up' : 'Click / Space'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * First non-empty string of the arguments — the model may name the talker as
 * `speaker` (this line), `caller` (this contact) or `name` (this call), and the
 * card prints whichever is most specific. Never invents one.
 * @param {...any} vals
 * @returns {string}
 */
function firstString(...vals) {
  for (const v of vals) if (typeof v === 'string' && v) return v;
  return '';
}

/**
 * The disc's letter. `font-display` is unreadable below ~14 px so this is the
 * one place it may carry a single glyph at size — never body copy.
 * @param {string} who
 * @returns {string}
 */
function initialOf(who) {
  const c = (who || '?').trim().charAt(0);
  return c ? c.toUpperCase() : '?';
}

/**
 * Drops any key the sim thinks is held. Reached through `engine.systems`, the
 * same way `ShopPanel.jsx`, `Cutscene.jsx` and `PauseMenu.jsx` do it.
 * @param {any} engine
 */
function clearGameInput(engine) {
  const systems = engine?.systems;
  if (!Array.isArray(systems)) return;
  for (const s of systems) {
    if (s?.name === 'gameplay') s.input?.clear?.();
  }
}
